import React, { createContext, useContext, useState, ReactNode, useEffect } from 'react';

interface AutoMatchSettings {
  enabled: boolean;
  barcodeMatching: boolean;
  nameMatching: boolean;
  nameSimilarityThreshold: number; // 0-100 percentage
}

interface AutoMatchContextType {
  autoMatchEnabled: boolean;
  setAutoMatchEnabled: (enabled: boolean) => void;
  settings: AutoMatchSettings;
  updateSettings: (settings: Partial<AutoMatchSettings>) => void;
}

const DEFAULT_SETTINGS: AutoMatchSettings = {
  enabled: false,
  barcodeMatching: true,
  nameMatching: false,
  nameSimilarityThreshold: 80
};

const AutoMatchContext = createContext<AutoMatchContextType | undefined>(undefined);

export const useAutoMatch = () => {
  const context = useContext(AutoMatchContext);
  if (context === undefined) {
    throw new Error('useAutoMatch must be used within an AutoMatchProvider');
  }
  return context;
};

interface AutoMatchProviderProps {
  children: ReactNode;
}

export const AutoMatchProvider = ({ children }: AutoMatchProviderProps) => {
  const [settings, setSettings] = useState<AutoMatchSettings>(() => {
    // Load from localStorage if available
    const saved = localStorage.getItem('autoMatchSettings');
    return saved ? { ...DEFAULT_SETTINGS, ...JSON.parse(saved) } : DEFAULT_SETTINGS;
  });

  // Legacy compatibility - keep autoMatchEnabled for existing components
  const autoMatchEnabled = settings.enabled;
  
  const setAutoMatchEnabled = (enabled: boolean) => {
    updateSettings({ enabled });
  };

  const updateSettings = (newSettings: Partial<AutoMatchSettings>) => {
    setSettings(prev => {
      const updated = { ...prev, ...newSettings };
      localStorage.setItem('autoMatchSettings', JSON.stringify(updated));
      return updated;
    });
  };

  return (
    <AutoMatchContext.Provider value={{ 
      autoMatchEnabled, 
      setAutoMatchEnabled, 
      settings, 
      updateSettings 
    }}>
      {children}
    </AutoMatchContext.Provider>
  );
};
